// Carrying the CSR's work onto another suggestion (2026-10-28,
// docs/contact-view.md "Confirm prompts"). One function for both places
// the panel does it: the park restore (overlayWork() in SuggestionPanel.jsx)
// and the keep button ("Keep my work" / "Keep my saved work") after a
// Regenerate or a step refresh for the same customer message, which moves the reply, the step inputs, the "My reply
// doesn't need this" marks and the task choice with its date onto the new
// suggestion. The choice is recorded on the new suggestion again, because
// Send checks it there. Pure apart from the setters and the recording call
// passed in, so node --test covers it.

import { taskPathChoiceStep, effectiveTaskPathChoice, isIsoDate } from '@cxhub/shared'

// A Send, Process, Close or Spam that reached a suggestion a Regenerate
// replaced (a stale tab, 2026-10-28). The work never moves by itself: Send
// opens the change comparison (the park compare panel) so the CSR sees what
// changed and chooses; Process, Close and Spam only say so, with "Load new
// draft" to get there.
export const REGENERATED_COMPARE_TITLE = 'This contact was regenerated. Check what changed, choose, then Send again'
export const REGENERATED_KEPT_TEXT = 'This contact was regenerated. The steps and the reply on the new suggestion are the same, so your work stays. Check it and Send again.'
export const REGENERATED_NOTICE_TEXT = 'This contact was regenerated. Check the new suggestion and try again.'
export const REGENERATED_PROCESSED_NOTE = 'Processed on the earlier suggestion: not offered again.'
export const REGENERATED_SEND_BLOCK = 'Choose what to keep from the new suggestion first, then Done.'

// While the regenerated comparison is open, Send and Process (a step's, and
// Process all) wait for its Done, with this line; Close stays available.
// parkCompare: the panel's compare state ({ source: 'regenerated' } for it).
export function regeneratedCompareBlock(parkCompare) {
  return parkCompare?.source === 'regenerated' ? REGENERATED_SEND_BLOCK : null
}
export const TASK_DATE_SAVE_ERROR = "Couldn't save the date. Try again."
export const TASK_CHOICE_SAVE_ERROR = "Couldn't save the choice. Try again."

// Whether `fresh` answers the same customer message as `current` (a
// Regenerate, a step refresh), not a new one. A suggestion without a trigger
// message counts as the same.
export function isSameCustomerTurn(current, fresh) {
  const id = fresh?.triggerMessageId ?? null
  return !id || !current || id === current.triggerMessageId
}

// The task choice to record on `target` so it holds `carried` ({ choice,
// date }): { index, choice, date }, or null when the target has no choice
// step, does not offer that choice, or already holds the same choice and
// date (recorded, or its preselection).
export function choiceToRecord(target, carried) {
  if (!carried?.choice) return null
  const actions = target?.proposedActions ?? []
  const step = taskPathChoiceStep(actions)
  const index = step ? actions.indexOf(step) : -1
  if (index < 0) return null
  if (!(step.params?.options ?? []).some((o) => o?.value === carried.choice)) return null
  const date = isIsoDate(carried.date) ? carried.date : null
  const held = effectiveTaskPathChoice(target)
  if (held?.choice === carried.choice && (held?.date ?? null) === date) return null
  return { index, choice: carried.choice, date }
}

// Send records the panel's choice first when it differs from the one the
// suggestion holds (the server checks the recorded one). A date the panel
// does not hold (an empty field) never clears a recorded one here.
export function choiceToSyncOnSend(suggestion, taskChoice, taskChoiceDate) {
  if (!taskChoice) return null
  const held = effectiveTaskPathChoice(suggestion)
  const date = isIsoDate(taskChoiceDate) ? taskChoiceDate : (held?.choice === taskChoice ? (held?.date ?? null) : null)
  return choiceToRecord(suggestion, { choice: taskChoice, date })
}

// Puts the carried step work (restoredWorkFor()'s result, `r`) on the panel
// and records the task choice on `target` when it differs. Returns the
// recording's promise, or null when nothing needed recording.
export function carryStepWork(r, target, { setActionInputs, setNotNeededSteps, setPartLinks, setTaskChoice, setTaskChoiceDate, recordChoice }) {
  setActionInputs((prev) => ({ ...prev, ...r.actionInputs }))
  setNotNeededSteps(r.notNeededSteps)
  setPartLinks((prev) => ({ ...prev, ...r.partLinks }))
  if (!r.taskChoice?.choice) return null
  setTaskChoice(r.taskChoice.choice)
  setTaskChoiceDate(r.taskChoice.date ?? '')
  const rec = choiceToRecord(target, r.taskChoice)
  return rec ? recordChoice(rec.index, rec.choice, rec.date) : null
}
